import { ReactElement, useEffect, useState } from "react";
import { Col, Row } from "react-bootstrap";
import { BsInfoCircle } from "react-icons/bs";
import Moment from "react-moment";
import { useLocation } from "react-router-dom";
import AlertModal from "../../../components/alertModal/alertModal";
import CustomBreadcrumb from "../../../components/customBreadcrumb/CustomBreadcrumb";
import CustomStepper from "../../../components/customStepper/CustomStepper";
import EmptyPlaceholder from "../../../components/emptyPlaceholder/EmptyPlaceholder";
import MainLoader from "../../../components/loading/MainLoader";
import SearchBar from "../../../components/searchBar/SearchBar";
import Title from "../../../components/title/Title";
import TooltipPopup from "../../../components/tooltipPopup/TooltipPopup";
import WORK_GROUP_LIST from "../../../const/workGroupList";
import { IFaultDetails } from "../../../interfaces/faultDetails";
import { getFaultDetailsByRefNo } from "../../../services/fault";
import { getCustomerReferenceNumber } from "../../../services/localStorage";
import styles from "./FaultStatus.module.scss";

const FaultStatus = () => {
  const { state } = useLocation();
  const [loading, setLoading] = useState<boolean>(false);
  const [faultStatus, setFaultStatus] = useState<IFaultDetails>();
  const [errorMessage, setErrorMessage] = useState<string | undefined>(
    undefined
  );
  const [searchFaultId, setSearchFaultId] = useState<string>();

  useEffect(() => {
    if (state) {
      setSearchFaultId((state as any).srNumber);
    }
  }, [state]);

  useEffect(() => {
    if (searchFaultId) {
      const data = {
        faultReferenceNumber: searchFaultId.trim(),
        customerReferenceNumber: getCustomerReferenceNumber(),
      };
      setLoading(true);
      getFaultDetailsByRefNo(data)
        .then((data: IFaultDetails) => {
          setFaultStatus(data);
          setLoading(false);
        })
        .catch((err) => {
          setErrorMessage(err.message);
          setLoading(false);
        });
    }
  }, [searchFaultId]);

  const searchHandler = (value: string) => {
    if (!value) {
      return;
    }

    setSearchFaultId(value);
  };

  const detailsItem = (title: string, value: string | ReactElement | null) => (
    <>
      {value && (
        <Row>
          <Col className={[styles.detailsItem].join(" ")}>
            <p className={styles.title}>{title}</p>
            <div className={styles.value}>{value}</div>
          </Col>
        </Row>
      )}
    </>
  );

  const stepperLabel = (
    title: string,
    isProcessing: boolean = false,
    value?: string | ReactElement
  ) => {
    return (
      <Row className={styles.label}>
        <Col className="text-start">
          <span className={styles.title}>{title} </span>
          {value && (
            <>
              <br />
              <span className={styles.value}>{value}</span>
            </>
          )}
          {isProcessing && (
            <>
              <br />
              <span className={styles.processing}>Processing...</span>
            </>
          )}
        </Col>
      </Row>
    );
  };

  const isStatusInWorkGroup = () => {
    if (!faultStatus) {
      return false;
    }

    return WORK_GROUP_LIST.map((item) => {
      return item["group"];
    }).includes(faultStatus.workGroup);
  };

  const getShortAddress = (address: string | null): string => {
    if (!address) {
      return "";
    }
    const addressArray = address.split(",");
    return addressArray[addressArray?.length - 2];
  };

  return (
    <div className="page-container">
      <MainLoader show={loading} />
      <CustomBreadcrumb
        items={[
          { path: "/", title: "Home" },
          { path: "/fault/status", title: "Complaint Status" },
        ]}
      />
      <Title>Complaint Status</Title>
      <Row className="justify-content-center mt-2">
        <Col className="col-md-5">
          <SearchBar
            placeholder="Fault Reference"
            onSearch={searchHandler}
            defaultValue={searchFaultId}
          />
        </Col>
      </Row>

      <Row>
        <Col className={[styles.container, "mt-4 py-5 px-5"].join(" ")}>
          {faultStatus ? (
            <>
              <Row className="mb-3">
                <Col>
                  {detailsItem(
                    "Branch",
                    <>
                      <Row className="align-items-center">
                        <Col className="col-auto pe-0">
                          {getShortAddress(faultStatus?.serviceAddress)}
                        </Col>
                        <Col className="col-auto pb-1">
                          <TooltipPopup
                            tooltip={faultStatus?.serviceAddress || ""}
                            placement="top"
                          >
                            <BsInfoCircle size={15} color="#818285" />
                          </TooltipPopup>
                        </Col>
                      </Row>
                    </>
                  )}
                </Col>
                <Col className="col-auto">
                  {detailsItem("ETR", faultStatus?.etr)}
                </Col>
              </Row>
              <CustomStepper
                steps={[
                  {
                    label: stepperLabel(
                      "Reported:",
                      false,
                      <Moment format="DD/MM HH:mm a">
                        {new Date(faultStatus?.reportedDate)}
                      </Moment>
                    ),
                    completed: true,
                    processing: false,
                  },
                  {
                    label: stepperLabel(
                      "Troubleshooting by SLT HQ technical Teams",
                      isStatusInWorkGroup()
                    ),
                    completed:
                      (faultStatus?.status === "OPEN" &&
                        !isStatusInWorkGroup()) ||
                      faultStatus?.status === "CLOSED",
                    processing:
                      faultStatus?.status === "OPEN" && isStatusInWorkGroup(),
                  },
                  {
                    label: stepperLabel(
                      "Troubleshooting by Field team",
                      faultStatus?.status === "OPEN" && !isStatusInWorkGroup()
                    ),
                    completed: faultStatus?.status === "CLOSED",
                    processing:
                      faultStatus?.status === "OPEN" && !isStatusInWorkGroup(),
                  },
                ]}
              />
            </>
          ) : (
            <EmptyPlaceholder />
          )}
        </Col>
      </Row>

      {/* error message */}
      <AlertModal
        show={!!errorMessage}
        buttons={[
          {
            variant: "danger",
            text: "OK",
            onClick: () => {
              setErrorMessage(undefined);
            },
          },
        ]}
        variant={"error"}
      >
        {errorMessage}
      </AlertModal>
    </div>
  );
};

export default FaultStatus;
